import { cn } from 'cn';
import { type PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { i18n } from '~/utils';

import { Button, type ButtonProps } from '~/ui/molecules/Button';

type ActionProps = ButtonProps;

const primaryActionDefault: ActionProps = {
  color: 'primary',
  size: 'large',
  text: i18n.t('button.primary.default', { ns: 'forms' }),
  title: undefined,
  type: 'button',
  variant: 'contained',
};

const secondaryActionDefault: ActionProps = {
  text: i18n.t('button.secondary.default', { ns: 'forms' }),
  variant: 'text',
};

const tertiaryActionDefault: ActionProps = {
  color: 'error',
  style: { marginLeft: 'auto' },
  text: i18n.t('button.tertiary.default', { ns: 'forms' }),
  variant: 'outlined',
};

type ActionsRowProps = PropsWithChildren<{
  readonly onCancel?: () => void;
  readonly primaryAction: ActionProps;
  readonly secondaryAction?: ActionProps;
  readonly secondaryActionComponent?: ReactNode;
  readonly shouldBeHidden?: boolean;
  readonly tertiaryAction?: ActionProps;
}> &
  ComponentStyling;

/**
 * Renders a row with primary, secondary, and tertiary actions.
 *
 * @param {PropsWithChildren<{
 *   onCancel?: () => void;
 *   primaryAction?: ActionProps;
 *   secondaryAction?: ActionProps;
 *   shouldBeHidden?: boolean;
 *   style?: React.CSSProperties;
 *   tertiaryAction?: ActionProps;
 * }> & ComponentStyling} props - The props for the ActionsRow component.
 * @return {JSX.Element} The rendered actions row component.
 */
export const ActionsRow = ({
  children,
  className,
  onCancel,
  primaryAction,
  secondaryAction,
  secondaryActionComponent,
  shouldBeHidden = false,
  style,
  tertiaryAction,
}: ActionsRowProps) => {
  const { t } = useTranslation('forms');

  const primaryActionProps: ActionProps = {
    ...primaryActionDefault,
    ...primaryAction,
  };
  if (primaryActionProps.disabled) {
    primaryActionProps.title = [
      primaryActionProps.title,
      t(($) => $.button.disabledTooltip, {
        ns: 'forms',
      }),
    ]
      .filter(Boolean)
      .join(' ');
    primaryActionProps.style = {
      ...primaryActionProps.style,
      cursor: 'not-allowed',
    }; // Does not work with Material UI b/c of pointer-events: none;
  }

  const { ...secondaryActionProps }: ActionProps = {
    ...secondaryActionDefault,
    onClick: onCancel,
    ...secondaryAction,
  };

  const { ...tertiaryActionProps }: ActionProps = {
    ...tertiaryActionDefault,
    ...tertiaryAction,
  };

  return (
    <div
      className={cn('flex w-full items-center gap-4 p-4', className)}
      style={style}
    >
      <Button {...primaryActionProps} />
      {onCancel && !shouldBeHidden ? (
        <Button {...secondaryActionProps} />
      ) : null}
      {secondaryActionComponent && !shouldBeHidden
        ? secondaryActionComponent
        : null}
      {tertiaryAction ? <Button {...tertiaryActionProps} /> : null}
      {children}
    </div>
  );
};
